import React, { useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import "./Login.css";

const Login = ({ onLogin }) => {
  const { login, pendingGiftCode } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const redirectPath =
    params.get("redirect") || location.state?.from || "/member";

  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState(
    params.get("reason") === "session-expired"
      ? "Your session expired. Please sign in again."
      : ""
  );
  const [verificationNeeded, setVerificationNeeded] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResendMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/login`,
        credentials
      );

      // Check if password change is required
      const passwordChangeRequired = response.data.passwordChangeRequired;

      // If onLogin prop is provided, use it (from App.js)
      if (onLogin) {
        await onLogin(response.data);
      } else {
        await login(response.data);

        // Redirect to change password page if required
        if (passwordChangeRequired) {
          navigate("/change-password");
        }
        // If there's a pending gift code, handle it
        else if (pendingGiftCode) {
          console.log("Found pending gift code:", pendingGiftCode);

          // Instead of trying to automatically redeem the gift here,
          // we'll redirect to the gift redemption page with a special flag
          // This is more reliable than trying to handle CSRF tokens here
          console.log(
            "Redirecting to gift redemption page with code:",
            pendingGiftCode
          );

          // Don't clear the pending gift code yet - we'll do that after successful redemption
          navigate(`/redeem/${pendingGiftCode}`, {
            state: { fromLogin: true },
          });
        } else {
          // Otherwise redirect to the original destination
          navigate(redirectPath);
        }
      }
    } catch (err) {
      console.error(err);

      // Check if this is an email verification error
      if (
        err.response?.status === 403 &&
        err.response?.data?.needsVerification
      ) {
        setVerificationNeeded(true);
        setUserEmail(err.response.data.email);
        setError(err.response.data.message);
      } else {
        setError(
          err.response?.data?.message || "Login failed. Please try again."
        );
      }
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    setResendMessage("");
    setResendingEmail(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/resend-verification`,
        { email: userEmail }
      );

      setResendMessage(response.data.message);
    } catch (err) {
      setResendMessage(
        err.response?.data?.message || "Failed to resend verification email."
      );
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <h2 className="login-title">Sign In</h2>

        {error && <p className="error-message">{error}</p>}
        {resendMessage && <p className="success-message">{resendMessage}</p>}

        {verificationNeeded ? (
          <div className="verification-needed">
            <p>Please verify your email address before logging in.</p>
            <button
              onClick={handleResendVerification}
              className="resend-button"
              disabled={resendingEmail}
            >
              {resendingEmail ? "Sending..." : "Resend Verification Email"}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                name="username"
                id="username"
                value={credentials.username}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                name="password"
                id="password"
                value={credentials.password}
                onChange={handleChange}
                required
              />
            </div>
            <div className="forgot-password">
              <Link to="/forgot-password">Forgot Password?</Link>
            </div>
            <button type="submit" className="login-button">
              Sign In
            </button>
          </form>
        )}

        <p className="signup-text">
          Don't have an account? <Link to="/register">Register here</Link>.
        </p>
      </div>
    </div>
  );
};

export default Login;
